import { useEffect } from "react";
import type { CSSProperties, RefObject } from "react";

import {
  PANEL_BRIDGE_ITEM_OVERLAP,
  PANEL_BRIDGE_MIN_WIDTH,
  PANEL_BRIDGE_WIDTH_BUFFER,
  PANEL_GAP,
  VIEWPORT_PAD,
} from "./constants";

export interface HoverAnchor {
  itemTop: number;
  itemBottom: number;
  itemLeft: number;
  itemRight: number;
}

export type PanelSide = "left" | "right";

export interface BridgeMetrics {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface PanelPlacement {
  left: number;
  side: PanelSide;
}

export const useStripInert = (ref: RefObject<HTMLElement | null>) => {
  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return;
    }

    const strip = () => {
      element.removeAttribute("inert");
      element.removeAttribute("aria-hidden");
    };

    strip();

    const observer = new MutationObserver(strip);
    observer.observe(element, {
      attributes: true,
      attributeFilter: ["inert", "aria-hidden"],
    });

    return () => observer.disconnect();
  });
};

export const getHoverAnchor = (rect: DOMRect): HoverAnchor => ({
  itemTop: rect.top,
  itemBottom: rect.bottom,
  itemLeft: rect.left,
  itemRight: rect.right,
});

export const getPanelTop = (
  hoverAnchor: HoverAnchor,
  panelHeight: number,
  viewportHeight: number,
) => {
  const fitsBelow = hoverAnchor.itemTop + panelHeight + VIEWPORT_PAD <= viewportHeight;
  return fitsBelow
    ? hoverAnchor.itemTop
    : Math.max(VIEWPORT_PAD, viewportHeight - VIEWPORT_PAD - panelHeight);
};

export const getPanelLeft = (panelStyle: CSSProperties, hoverAnchor: HoverAnchor | null) => {
  if (typeof panelStyle.left === "number") {
    return panelStyle.left;
  }

  if (!hoverAnchor) {
    return 0;
  }

  return hoverAnchor.itemRight + PANEL_GAP;
};

export const getPanelHeight = (panelElement: HTMLDivElement | null) =>
  panelElement?.offsetHeight ?? 200;

export const getBridgeMetrics = ({
  hoverAnchor,
  panelSide,
  panelLeft,
  panelTop,
  panelWidth,
  panelHeight,
}: {
  hoverAnchor: HoverAnchor | null;
  panelSide: PanelSide;
  panelLeft: number;
  panelTop: number;
  panelWidth: number;
  panelHeight: number;
}): BridgeMetrics => {
  if (!hoverAnchor) {
    return {
      top: 0,
      left: 0,
      width: PANEL_BRIDGE_MIN_WIDTH,
      height: panelHeight,
    };
  }

  const left =
    panelSide === "right"
      ? hoverAnchor.itemRight - PANEL_BRIDGE_ITEM_OVERLAP
      : panelLeft + panelWidth - PANEL_BRIDGE_ITEM_OVERLAP;
  const width = Math.max(
    PANEL_BRIDGE_MIN_WIDTH,
    panelSide === "right"
      ? panelLeft - hoverAnchor.itemRight + PANEL_BRIDGE_WIDTH_BUFFER
      : hoverAnchor.itemLeft - (panelLeft + panelWidth) + PANEL_BRIDGE_WIDTH_BUFFER,
  );

  return {
    top: Math.min(hoverAnchor.itemTop, panelTop),
    left,
    width,
    height: Math.abs(hoverAnchor.itemTop - panelTop) + panelHeight,
  };
};

export const isOverHoverAnchor = (x: number, y: number, hoverAnchor: HoverAnchor | null) => {
  if (!hoverAnchor) {
    return false;
  }

  return (
    x >= hoverAnchor.itemLeft &&
    x <= hoverAnchor.itemRight &&
    y >= hoverAnchor.itemTop &&
    y <= hoverAnchor.itemBottom
  );
};

export const resolvePanelPlacement = (
  hoverAnchor: HoverAnchor,
  panelWidth: number,
  viewportWidth: number,
): PanelPlacement => {
  const rightLeft = hoverAnchor.itemRight + PANEL_GAP;
  const leftLeft = hoverAnchor.itemLeft - PANEL_GAP - panelWidth;
  const canPlaceRight = rightLeft + panelWidth + VIEWPORT_PAD <= viewportWidth;
  const canPlaceLeft = leftLeft >= VIEWPORT_PAD;

  if (canPlaceRight) {
    return { left: rightLeft, side: "right" };
  }

  if (canPlaceLeft) {
    return { left: leftLeft, side: "left" };
  }

  const clampedLeft = Math.max(
    VIEWPORT_PAD,
    Math.min(rightLeft, viewportWidth - panelWidth - VIEWPORT_PAD),
  );
  const side = clampedLeft >= hoverAnchor.itemLeft ? "right" : "left";
  return { left: clampedLeft, side };
};

const isOverElement = (x: number, y: number, element: HTMLElement | null) => {
  if (!element) {
    return false;
  }

  const rect = element.getBoundingClientRect();
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
};

export const isCursorOverPortal = (portalElement: HTMLDivElement | null, x: number, y: number) => {
  if (!portalElement) {
    return false;
  }

  return Array.from(portalElement.children).some(child =>
    isOverElement(x, y, child as HTMLElement),
  );
};
